/**
 * @license
 * Foundation.js
 * (c) 2012 DesertNet. All rights reserved.
 * Visit http://www.publishwithfoundation.com/
 */

import nfp from './nativizeFoundationPromise.js'

/**
 * @namespace The Foundation namespace.
 */
window.Foundation = {};
window["Foundation"] = {};

/**
 * @const True when running on devel host. Might want to make this an @define someday.
 * @type {boolean}
 */
Foundation.DEVEL = document.location.host.match(/\bdevel\b/) ? true : false;

/**
 * @define {boolean} True if we're using the core mobile JS file.
 */
Foundation.MOBILE = window.__FOUNDATION_MOBILE__ ? true : false;

try {
  Foundation.RESPONSIVE = window.__FOUNDATION_RESPONSIVE__ ? true : false
} 
catch (e) {
  // eslint-disable-next-line no-console
  console.log(e)
}

/**
 * @const List of valid Foundation subdomains. This allows cookies
 * to be created on one of these subdomains but still be accessible
 * to all domains that belong to this site.
 */
Foundation.SUBDOMAINS = [
    "admin", "www", "posting", "a", "m", "p", // Standard Foundation subdomains
    "post", "edit", "blog",                   // Legacy subdomains
    "blogtown", "endhits", "mod", "tba",      // Portland Mercury blog subdomains
    "slog", "lineout"                         // The Stranger blog subdomains
];

/**
 * Copy the properties of one object to another.
 * @param {Object} destination
 * @param {Object=} source
 * @return {Object}
 */
Foundation.extend = function (destination, source) {
    // Preserve the destination's constructor.
    var destinationConstructor = destination.constructor;

    // Copy properties.
    for (var property in source) {
        if (Object.prototype.hasOwnProperty.call(source, property)) {
            destination[property] = source[property];
        }
    }

    // Restore the destination's constructor assuming it's not just an Object.
    if (destinationConstructor !== Object)
        destination.constructor = destinationConstructor;

    return destination;
};

/**
 * Inherit from one class to another. When using this function, you'll likely
 * want to call the superclass's constructor using <code>.call(this)</code>.
 * @param {Function} subclass
 * @param {Function} superclass
 */
Foundation.inherit = function (subclass, superclass) {
    // Yes, it is a bit strange, but this is how you make
    // classical inheritance work under the prototypical
    // inheritance model.
    if (Object.create && Object.create.length === 2) {
        subclass.prototype = Object.create(superclass.prototype, {
            "constructor": {"value": subclass, "writable": true, "configurable": true}
        });
    }
    else {
        // For browsers that don't have Object.create.
        /** @constructor */ var dummyConstructor = function () {};
        dummyConstructor.prototype = superclass.prototype;
        subclass.prototype = new dummyConstructor();
        subclass.prototype.constructor = subclass;
    }
};

/**
 * Determine if an object is actually a constructor function
 * and inherits from a specified class.
 * @param {*} possibleConstructor
 * @param {Function} expectedSuperclass
 * @return {boolean}
 */
Foundation.verifyObjectIsConstructorAndInheritsFrom = function (possibleConstructor, expectedSuperclass) {
    if (!(possibleConstructor instanceof Function)) {
        return false;
    }

    // Here's some more "cleverness". We can't just travel up the
    // prototype chain to see if the function actually inherits
    // from expectedSuperclass. So we need to trick the JS
    // interpreter into doing this for us. So we create a dummy
    // constructor function that does nothing, and that inherits
    // from the possibleConstructor function. We can then use
    // instanceof to check for inheritance.
    /** @constructor */ var dummyConstructor = function () {};
    Foundation.inherit(dummyConstructor, possibleConstructor);
    var dummyInstance = new dummyConstructor();
    return (dummyInstance instanceof expectedSuperclass);
};

/**
 * Create a version of a function with parameter values burned in. Works the
 * same as Prototype's Function.prototype.curry().
 * @param {Function} f
 * @param {...*} var_args
 */
Foundation.curry = function (f) {
    if (arguments.length === 1) return f;
    var curriedArgs = Array.prototype.slice.call(arguments, 1);
    return function () {
        var args = Array.prototype.slice.call(arguments, 0);
        return f.apply(this, curriedArgs.concat(args));
    };
};

/**
 * Warning logging convenience function.
 * @param {...*} var_args
 */
Foundation.warn = function () {
    var args = Array.prototype.slice.call(arguments, 0);
    if (window.console && window.console.warn) {
        // IE8 and IE9's console object functions don't have an apply method.
        // So we need to check for that as well.
        if (window.console.warn.apply) {
            window.console.warn.apply(window.console, args);
        }
        else {
            // This is less than ideal. A better method is described here:
            // http://urlpixie.com/ogo
            window.console.warn(`${args[0]}; args: ${args.slice(1).join(", ")}`);
        }
    }
    else if (Foundation.Dev.isDebugJSActive()) {
        // No console and we're in debug mode (aka IE7), so let's display
        // an alert.
        window.alert(`Foundation.warn: ${args[0]}; args: ${args.slice(1).join(", ")}`);
    }
};

/**
 * Iterate over values of an object.
 * @param {Object} obj
 * @param {function(*,number=,*=)} iterator
 */
Foundation.each = function (obj, iterator) {
    var i = 0;
    for (var key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
            var breakWhenFalse = iterator(obj[key], i, key);
            if (breakWhenFalse === false) {
                break;
            }
            ++i;
        }
    }
};

/**
 * Test whether an argument is a string.
 * @param {*} possibleString
 * @return {boolean}
 */
Foundation.isString = function (possibleString) {
    if (typeof possibleString === "string") return true;
    if (possibleString.constructor === String) return true;
    return false;
};

/**
 * Returns the number of milliseconds since the epoch.
 * @return {number}
 */
Foundation.currentTime = function () {
    if (Foundation.Dev.isTestingMode()) {
        return 1361573798518;
    }
    else {
        return (new Date()).getTime();
    }
};


/**
 * Detect if the page being served is from the mobile domain.
 * @return {boolean}
 */
Foundation.domainIsMobile = function () {
    return !!(document.domain.match(/^[amp]\./i));
};


/**
 * Stuff to run on jQuery Mobile pageinit events.
 */
Foundation.pageInit = function (e) {
    // Scroll to the anchor specified by the scrollto URL paramater. Need this
    // since jQuery Mobile makes URL hashes useless for their traditional
    // usage.
    Foundation.windowLoaded.then(function () {
        (new Foundation.Timer(200)).then(function () {
            var url = Foundation.URL.fromCurrentLocation();
            var anchorName = url.get("scrollto");
            if (anchorName) {
                var page = jQuery(e.target);
                var anchor = page.find(`[id=${anchorName}]`);
                if (!anchor) anchor = page.find(`[name=${anchorName}]`);
                if (anchor[0]) {
                    jQuery.mobile.silentScroll(anchor.offset()["top"] - 10);
                }
            }
        });
    });
};

// Set up pageinit handler.
if (Foundation.MOBILE) {
    jQuery(document).on("pageinit", "div[data-role=page]", Foundation.pageInit);
}

/**
 * Convenience for handling differences between browser's event models.
 * @param {*} domObj DOM object such as window, document, document.body or other elements
 * @param {string} eventName Name of the event to listen for.
 * @param {Function} eventHandler The function to handle the event.
 */
Foundation.addEventListener = function (domObj, eventName, eventHandler) {
    if (domObj.addEventListener) {
        domObj.addEventListener(eventName, eventHandler, false)
    }
    else if (domObj.attachEvent) {
        domObj.attachEvent(eventName, function () {
            eventHandler(window.event)
        })
    }
    else {
        throw new Error("Non-DOM object passed to Foundation.addEventListener")
    }
};

/**
 * @param {string} lib
 * @return {Promise}
 */
Foundation.load = function (lib) {
  const jsBranchCookie = new Foundation.Cookie('foundation-js-branch')
  const branch = jsBranchCookie.getValue() || 'master'
  const branchDir = branch === 'master' ? '' : `foundation-js/${encodeURIComponent(branch)}/`

  const prefix = window['_webpackServerUrl'] || `/foundation/scripts/${branchDir}`
  const queryString = `?cb=${CORE_JS_BUILD_NUMBER}`

  const allowedLibs = {
    "foundation-login-dialog": true,
    "foundation-dev-branchswitcher": true,
  }

  if (!allowedLibs[lib]) {
    throw new Error(`${lib}: not an allowed library identifier`)
  }

  const scriptUrl = `${prefix}${lib}.js${queryString}`
  return nfp(() => new Foundation.Script(scriptUrl, true))
}

/**
 * Load legacy libraries (Prototype and Scriptaculous Effects). This must be
 * called inside its own script element — you won't see the libraries until
 * after the closing script tag. Keep in mind this blocks page rendering until
 * the scripts have been loaded, so always make an effort to use a different
 * approach that does not rely on legacy libraries.
 */
Foundation.loadLegacyLibraries = function () {
  if (window['Prototype'] === undefined) {
    new Foundation.Script('/foundation/scripts/prototype-compiled.js')
  }

  if (window['Scriptaculous'] === undefined) {
    new Foundation.Script('/foundation/scripts/scriptaculous/scriptaculous.js?load=')
    new Foundation.Script('/foundation/scripts/scriptaculous/effects.js')
  }
}

// ---------------------------------------------------------------------

window["Foundation"]["inherit"] = Foundation.inherit
window["Foundation"]["load"] = Foundation.load
window["Foundation"]["loadLegacyLibraries"] = Foundation.loadLegacyLibraries
